import { filter, map, mergeMap, tap } from 'rxjs/operators'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { Events } from 'depoto-core/src/utils'
import { cookieConsent, HistoryUtil, SchemaUtil } from './utils'
import { CoreService, NotificationService, TrialService } from './services'
import { Company, User } from 'depoto-core/src/entities'
import { NgSelectConfig } from '@ng-select/ng-select'
import * as Sentry from '@sentry/angular-ivy'
import { TranslateService } from '@ngx-translate/core'
import { interval, Observable, Subscription } from 'rxjs'
import { environment } from '../environments/environment'
import packageJson from '../../package.json'
import { HttpClient } from '@angular/common/http'
import { Select } from '@ngxs/store'
import { GoogleTagManagerService } from 'angular-google-tag-manager'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  company: Company
  appVersionCheckInterval: any = null
  isShutdownForOverdue = false
  isLoading = false
  isBaseModelLoaded = false
  private subscriptions = new Subscription()
  public isNewVersionAvailable = false
  @Select(state => state.plugplay?.isWizardVisible)
  public isWizardVisible: Observable<boolean>

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private core: CoreService,
    private translateService: TranslateService,
    private ngSelectConfig: NgSelectConfig,
    private readonly httpClient: HttpClient,
    public trialService: TrialService,
    private gtmService: GoogleTagManagerService,
  ) {
    this.checkVersion()

    window['DEBUG'] = {
      exception: false,
      logger: false,
      history: false,
    }
    // todo decide when is all loaded in, on every page
    Events.listen('init:loading', () => {
      this.isLoading = false
      this.isBaseModelLoaded = true
    })
    this.core.services.user.currentUserEmitter.subscribe().then((user: User) => {
      if (user?.email && user?.locale) {
        this.translateService.use(user.locale)
        Sentry.setUser({ id: user.id, username: `${user.name} ${user.lastName}`, email: user.email })
        Sentry.setTag('company', user.company.name)
      } else {
        Sentry.setUser(null)
        Sentry.setTag('company', 'undefined')
      }
    })
    this.subscriptions.add(
      this.translateService.onLangChange
        .pipe(
          tap(() => {
            this.initI18N()
            cookieConsent(this.gtmService, this.translateService)
          }),
        )
        .subscribe(),
    )
    this.core.onCoreEntityListsLoaded.subscribe(() => {
      this.isBaseModelLoaded = true
    })
  }

  private checkVersion(): void {
    const url = this.getUrlForCheckingAppVersion()
    if (!url) {
      // don't check for latest version on localhost
      return
    }
    this.httpClient.get<{ version: string; date: string }>(url).subscribe(resp => {
      this.isNewVersionAvailable = packageJson.version !== resp.version
    })
    this.subscriptions.add(
      interval(2 * 60 * 1000)
        .pipe(
          mergeMap(() => {
            if (document.hasFocus()) {
              return this.httpClient.get<{ version: string; date: string }>(url)
            } else {
              return Promise.resolve(null)
            }
          }),
        )
        .subscribe(response => {
          this.isNewVersionAvailable = response && response.version !== packageJson.version
        }),
    )
  }

  private getUrlForCheckingAppVersion(): string {
    let result
    switch (location.host) {
      case 'app.evidentist.cz.tomatomstage.cz':
        result = environment.appVersionUrlStage
        break
      case 'app.evidentist.cz':
        result = environment.appVersionUrlProduction
        break
    }
    return result
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  private initI18N(): void {
    this.ngSelectConfig.addTagText = this.translateService.instant('select-config.add-tag')
    this.ngSelectConfig.clearAllText = this.translateService.instant('select-config.clear-all')
    this.ngSelectConfig.loadingText = this.translateService.instant('select-config.loading')
    this.ngSelectConfig.notFoundText = this.translateService.instant('select-config.not-found')
    this.ngSelectConfig.typeToSearchText = this.translateService.instant('select-config.search')
  }

  async ngOnInit() {
    // this.gtmService.addGtmToDom()
    this.listenForRoutingUpdates()
    this.loadSideMenuState()
    this.core.services.oauth.onLogoutEmitter.subscribe().then(() => {
      window.localStorage.clear()
    })
  }

  public isAuthenticated(): boolean {
    return this.core.services.oauth.isAuthenticated()
  }

  listenForRoutingUpdates() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => this.route),
        map(route => {
          if (route.firstChild) {
            route = route.firstChild
          }
          return route
        }),
        filter(route => route.outlet === 'primary'),
      )
      .subscribe(route => {
        HistoryUtil.onRouteChange([route.snapshot.routeConfig.path, window.location.hash], this.core)
        this.dispatchAnalyticsEvent()
        const gtmTag = {
          event: 'page',
          pageName: route.snapshot.routeConfig.path,
        }
        this.gtmService.pushTag(gtmTag)
      })
  }

  loadSideMenuState() {
    const sidebarState: string = this.core.services.storage.getSync(this.core.services.storage.keys.app.sideMenuState)
    if (!sidebarState || sidebarState === 'collapsed') {
      document.getElementsByTagName('body')[0].className += ' sidebar-collapse'
    }
  }

  dispatchAnalyticsEvent() {
    // must be initialized from index.html
    const _paq = window['_paq'] || []
    _paq.push(['setReferrerUrl', window.location.href])
    const currentUrl = `${window.location.hash.substr(1)}`
    _paq.push(['setCustomUrl', currentUrl])
    _paq.push(['setDocumentTitle', document.title])
    _paq.push(['deleteCustomVariables', 'page'])
    _paq.push(['setGenerationTimeMs', 0])
    _paq.push(['trackPageView'])
    _paq.push(['MediaAnalytics::scanForMedia', document])
    _paq.push(['FormAnalytics::scanForForms', document])
    _paq.push(['trackContentImpressionsWithinNode', document])
    Events.dispatch('app:navigation-end')
  }

  async loadUserProfile() {
    let isUserLoaded = false
    setTimeout(async () => {
      let u
      try {
        u = await this.core.services.user.getMyProfile(SchemaUtil.userProfile)
        console.log('getuser', u)
      } catch (e) {
        if (e && e.status === 401) {
          console.log('e401: ', e.message)
        }
      }
      if (u && u.email) {
        this.core.services.storage.set(this.core.services.storage.keys.auth.companyCarriers, u.company.carrierRelations)
        this.core.services.user.setCurrentUser(u.email)
      } else {
        setTimeout(() => {
          this.core.services.oauth.logout()
        }, 500)
      }
    }, 10)
    this.core.services.user.currentUserEmitter.subscribe().then(user => {
      setTimeout(() => {
        isUserLoaded = user && user.id > 0
        if (isUserLoaded) {
          Events.dispatch('app:user:loaded')
          this.core.loadLists()
        }
      }, 100) // let event loop render the page, loader .. etc
    })
    this.core.onCoreEntityListsLoaded.subscribe(() => {
      this.isBaseModelLoaded = isUserLoaded
      if (!isUserLoaded) {
        setTimeout(() => (this.isBaseModelLoaded = true), 500)
      }
    })
  }

  public upgrade(): void {
    window.location.reload()
  }
}
